import type { TFunction } from "i18next";
import { Bell, CheckCheck, Clock, Palmtree, PiggyBank, ShieldAlert, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useMarkNotificationsRead, useNotifications, type AppNotification } from "@/api/queries";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip } from "@/components/ui/tooltip";
import { dateInZone, formatDayMonth, formatShortDateTime, formatTime } from "@/lib/format";
import { cn } from "@/lib/utils";

import { useCurrentUser } from "../auth";

const icons: Record<AppNotification["kind"], LucideIcon> = {
  manual_time_requested: Clock,
  manual_time_approved: Clock,
  manual_time_rejected: Clock,
  manual_time_cancelled: Clock,
  manual_time_added: Clock,
  vacation_set: Palmtree,
  vacation_cleared: Palmtree,
  team_vacation: Palmtree,
  budget_warning: PiggyBank,
  budget_exceeded: PiggyBank,
  anomalies_found: ShieldAlert,
};

/** Portal page a notification leads to (the same as the email link). */
// eslint-disable-next-line react-refresh/only-export-components
export function notificationLink(n: AppNotification): string {
  switch (n.kind) {
    case "manual_time_requested":
      return "/timesheets/manual?view=review";
    case "manual_time_rejected":
      return "/timesheets/manual";
    case "manual_time_approved":
    case "manual_time_cancelled":
    case "manual_time_added":
      return n.started_at ? `/timesheets?date=${n.started_at.slice(0, 10)}` : "/timesheets";
    case "vacation_set":
    case "vacation_cleared":
    case "team_vacation":
      return "/time-off";
    case "anomalies_found":
      return "/reports/anomalies";
    default:
      return n.project_id ? `/projects/${n.project_id}` : "/";
  }
}

/** One-line description of a notification. */
// eslint-disable-next-line react-refresh/only-export-components
export function notificationText(t: TFunction, n: AppNotification, lang: string, timeZone: string): string {
  const when =
    n.started_at && n.ended_at
      ? `${formatDayMonth(dateInZone(n.started_at, timeZone), lang)}, ${formatTime(n.started_at, lang, timeZone)}–${formatTime(n.ended_at, lang, timeZone)}`
      : "";
  const range = n.vacation_from
    ? n.vacation_until
      ? t("vacations.range", {
          from: formatDayMonth(n.vacation_from, lang),
          until: formatDayMonth(n.vacation_until, lang),
        })
      : t("vacations.rangeOpen", { from: formatDayMonth(n.vacation_from, lang) })
    : "";
  const vars = {
    actor: n.actor_name ?? "",
    user: n.user_name ?? "",
    project: n.project_name ?? "",
    comment: n.comment ?? "",
    percent: n.percent ?? 0,
    count: n.count ?? 0,
    when,
    range,
  };
  if (n.kind === "manual_time_rejected" && n.comment)
    return t("notifications.kinds.manual_time_rejected_comment", vars);
  return t(`notifications.kinds.${n.kind}`, vars);
}

/** The bell in the top bar: unread count and the latest notifications. */
export function NotificationsMenu() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const me = useCurrentUser();
  const navigate = useNavigate();
  const query = useNotifications();
  const markRead = useMarkNotificationsRead();
  const pages = query.data?.pages ?? [];
  const items = pages.flatMap((p) => p.items);
  const unread = pages[0]?.unread_count ?? 0;
  const label = unread > 0 ? t("notifications.unread", { count: unread }) : t("topbar.notifications");

  const open = (n: AppNotification) => {
    if (!n.read) markRead.mutate([n.id]);
    void navigate(notificationLink(n));
  };

  return (
    <DropdownMenu>
      <Tooltip content={label}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={label} className="relative">
            <Bell />
            {unread > 0 && (
              <span
                className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-solid px-1 text-[10px] leading-none font-semibold text-white tabular-nums"
                aria-hidden="true"
              >
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
      </Tooltip>
      <DropdownMenuContent align="end" className="w-[min(92vw,380px)] p-0">
        <div className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
          <span className="font-semibold">{t("notifications.title")}</span>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                markRead.mutate(undefined);
              }}
            >
              <CheckCheck /> {t("notifications.markAll")}
            </Button>
          )}
        </div>
        <div className="max-h-[60dvh] overflow-y-auto p-1">
          {query.isPending ? (
            <p className="px-3 py-6 text-center text-muted">{t("common.loading")}</p>
          ) : items.length === 0 ? (
            <p className="px-3 py-6 text-center text-muted">{t("notifications.empty")}</p>
          ) : (
            items.map((n) => {
              const Icon = icons[n.kind];
              return (
                <DropdownMenuItem
                  key={n.id}
                  onSelect={() => open(n)}
                  className="items-start gap-3 px-3 py-2.5"
                >
                  <span
                    className={cn(
                      "mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full",
                      n.kind === "budget_exceeded" ||
                        n.kind === "manual_time_rejected" ||
                        n.kind === "anomalies_found"
                        ? "bg-danger-soft text-danger"
                        : "bg-primary-soft text-primary",
                    )}
                  >
                    <Icon className="size-3.5" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={cn("block break-words", !n.read && "font-medium")}>
                      {notificationText(t, n, lang, me.timezone)}
                    </span>
                    <span className="text-xs text-muted">
                      {formatShortDateTime(n.created_at, lang, me.timezone)}
                    </span>
                  </span>
                  {!n.read && (
                    <span
                      className="mt-2 size-2 shrink-0 rounded-full bg-primary"
                      aria-label={t("notifications.new")}
                    />
                  )}
                </DropdownMenuItem>
              );
            })
          )}
          {query.hasNextPage && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              disabled={query.isFetchingNextPage}
              onClick={(e) => {
                e.preventDefault();
                void query.fetchNextPage();
              }}
            >
              {t("notifications.more")}
            </Button>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
